import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { ArrowRight, Menu, X, Phone } from "lucide-react";
import WhatsAppFAB from "./WhatsAppFAB";

const NAV = [
  { label: "Özellikler", href: "/ozellikler" },
  { label: "AI Maç Analizi", href: "/ai-futbol" },
  { label: "Sosyal Medya Yönetimi", href: "/ai-sosyal-medya" },
  { label: "Akademi", href: "/akademi" },
  { label: "İletişim", href: "/iletisim" },
  { label: "Fiyatlar", href: "/fiyatlandirma" },
];

export const Logo = ({ className = "" }) => (
  <Link to="/" className={`inline-flex items-baseline font-display font-black ${className}`} data-testid="mkt-logo">
    <span className="text-white text-[22px] leading-none tracking-tight">DR AI</span>
    <span className="text-dr-yellow text-[22px] leading-none tracking-tight ml-1.5">FUTBOL</span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-dr-black/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="mkt-navbar"
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />
        <div className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              className={({ isActive }) =>
                `text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "text-dr-yellow" : "text-white/75 hover:text-white"
                }`
              }
              data-testid={`mkt-nav-${n.href.slice(1)}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="tel:+905437934101"
            className="hidden md:inline-flex items-center gap-2 text-[12px] font-semibold text-white/70 hover:text-dr-yellow transition"
            data-testid="mkt-nav-phone"
          >
            <Phone size={14} strokeWidth={2.5} />
            +90 543 793 41 01
          </a>
          <Link
            to="/giris"
            className="hidden sm:inline-flex text-[12px] font-semibold uppercase tracking-wider text-white/60 hover:text-white transition"
            data-testid="mkt-nav-login"
          >
            Giriş
          </Link>
          <Link
            to="/uye-ol"
            className="mkt-btn-primary !py-2.5 !px-4 !text-[12px] hidden sm:inline-flex"
            data-testid="mkt-nav-cta"
          >
            Ücretsiz Üye Ol <ArrowRight size={16} strokeWidth={3} />
          </Link>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="mkt-nav-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-dr-black/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="flex flex-col p-6 gap-5">
              {NAV.map((n) => (
                <Link
                  key={n.href}
                  to={n.href}
                  className="text-white font-hero text-2xl uppercase"
                  data-testid={`mkt-nav-mobile-${n.href.slice(1)}`}
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/uye-ol" className="mkt-btn-primary justify-center w-full mt-4" data-testid="mkt-nav-mobile-cta">
                Ücretsiz Üye Ol <ArrowRight size={16} strokeWidth={3} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => (
  <footer className="relative bg-dr-black border-t border-white/5 pt-20 pb-10 mt-32" data-testid="mkt-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-14">
        <div className="col-span-2">
          <Link to="/" className="inline-flex items-baseline font-display font-black" data-testid="mkt-footer-logo">
            <span className="text-white text-[32px] leading-none tracking-tight">DR AI</span>
            <span className="text-dr-yellow text-[32px] leading-none tracking-tight ml-2">FUTBOL</span>
          </Link>
          <p className="text-white/55 text-sm max-w-sm mt-4 leading-relaxed">
            Türk futbol kulüpleri için yapay zeka destekli dijital yönetim platformu.
            Kadro, fikstür, kulüp kasası ve sosyal medya — tek panelde. Kurumsal web sitesi Pro pakete dahildir.
          </p>
        </div>
        <FooterCol
          title="Ürün"
          links={[
            ["Özellikler", "/ozellikler"],
            ["Fiyatlandırma", "/fiyatlandirma"],
            ["AI Maç Analizi", "/ai-futbol"],
            ["9 Instagram Şablonu", "/ai-sosyal-medya"],
            ["Kulüp Web Sitesi", "/web-sitesi"],
            ["Akademi", "/akademi"],
          ]}
        />
        <FooterCol
          title="Şirket"
          links={[
            ["Ücretsiz Üye Ol", "/uye-ol"],
            ["İletişim", "/iletisim"],
            ["Giriş", "/giris"],
          ]}
        />
        <FooterCol
          title="İletişim"
          links={[
            ["info@dijitalroket.com", "mailto:info@dijitalroket.com"],
            ["+90 543 793 41 01", "tel:+905437934101"],
            ["@dijital.roket", "https://instagram.com/dijital.roket"],
          ]}
          external
        />
      </div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-8 border-t border-white/5">
        <div className="flex flex-col gap-2">
          <span className="font-over text-white/30">
            © 2026 DR AI FUTBOL · Tüm hakları saklıdır
          </span>
          <span className="text-[11px] text-white/40">
            Bu web sitesi{" "}
            <a
              href="https://www.dijitalroket.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-dr-yellow hover:text-dr-yellow-hi underline underline-offset-2 font-semibold transition"
              data-testid="footer-credit-dijitalroket"
            >
              Dijital Roket
            </a>{" "}
            tarafından geliştirilmiştir.
          </span>
        </div>
      </div>
    </div>
  </footer>
);

const FooterCol = ({ title, links, external }) => (
  <div>
    <h4 className="font-over text-dr-yellow mb-5">{title}</h4>
    <ul className="space-y-3">
      {links.map(([label, href]) => (
        <li key={label}>
          {external || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("http") ? (
            <a href={href} className="text-sm text-white/65 hover:text-white transition">{label}</a>
          ) : (
            <Link to={href} className="text-sm text-white/65 hover:text-white transition">{label}</Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

/* Smart image with skeleton + error fallback */
export const SmartImg = ({ src, alt, className = "", fallbackLabel, ...rest }) => {
  const [state, setState] = useState("loading"); // loading | ok | err
  return (
    <>
      {state === "err" ? (
        <div className={`mkt-img-fallback ${className}`} aria-label={alt}>
          {fallbackLabel || "GÖRSEL HAZIRLANIYOR"}
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`${className} ${state === "loading" ? "opacity-0" : "opacity-100 transition-opacity duration-500"}`}
          onLoad={() => setState("ok")}
          onError={() => setState("err")}
          {...rest}
        />
      )}
    </>
  );
};

const MarketingLayout = ({ children }) => {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-dr-black text-white selection:bg-dr-yellow selection:text-dr-black">
      <Navbar />
      <main>{children}</main>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
};

export default MarketingLayout;
